import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { CheckCircle, XCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/checkout/status/${sessionId}`);

      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status check failed:', error);
      setStatus('error');
    }
  }, [sessionId, refreshUser]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(0);
  }, [sessionId, pollPaymentStatus]);

  const fadeUpAnimation = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="payment-success-page">
      <motion.div {...fadeUpAnimation} className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-outfit text-2xl text-white mb-2">Processing Payment</h1>
            <p className="text-zinc-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="font-outfit text-3xl text-white mb-2">Payment Successful!</h1>
            <p className="text-zinc-400 mb-8">
              Your subscription has been activated. Welcome to THE VAULT by 1000Banks community!
            </p>
            {paymentData && (
              <div className="bg-[#121212] border border-white/5 p-4 mb-8 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">Amount</span>
                  <span className="text-white font-mono">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Status</span>
                  <span className="text-green-500">{paymentData.payment_status}</span>
                </div>
              </div>
            )}
            <Link to="/dashboard">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] w-full">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="font-outfit text-2xl text-white mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-zinc-400 mb-8">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
            </p>
            <div className="flex gap-4">
              <Link to="/pricing" className="flex-1">
                <Button variant="outline" className="w-full border-zinc-700 text-white">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard" className="flex-1">
                <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                  Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
